import * as T from 'three';
import { materialPath, texturePath } from './binary.js';
import { Component } from './live-codec.js';
import { readResource, resourceProperties, resourceValue } from './vgui-res.js';

export function annotationPosition(state, scene) {
    const followed = scene?.entities.get(`${state.followIndex}:${state.followGeneration}`);
    if (!followed) return new T.Vector3(...state.position);
    const position = followed.object.getWorldPosition(new T.Vector3());
    // Native callouts follow the entity's origin plus its collision height.
    const box = new T.Box3().setFromObject(followed.object);
    position.z += box.isEmpty() ? 82 : box.max.z - box.min.z;
    return position;
}
export function projectAnnotation(position, camera, width, height, panelWidth = 200, panelHeight = 55) {
    const forward = camera.getWorldDirection(new T.Vector3());
    const delta = position.clone().sub(camera.position), projected = position.clone().project(camera);
    const x = (projected.x + 1) * width / 2, y = (1 - projected.y) * height / 2;
    const offscreen = delta.dot(forward) <= 0 || x < panelWidth / 2 || x > width - panelWidth / 2 || y < 0 || y > height;
    if (offscreen) {
        const left = delta.x * forward.y - delta.y * forward.x <= 0;
        return { x: left ? 20 : Math.max(20, width - panelWidth - 20), y: (height - panelHeight) / 2, arrow: left ? 'left' : 'right' };
    }
    return { x: Math.max(0, Math.min(width - panelWidth, x - panelWidth / 2)), y: Math.max(0, y - panelHeight - 10), arrow: 'down' };
}
export class LiveAnnotations {
    constructor({ root, cache, tokens = () => new Map(), diagnostic = () => {} }) {
        Object.assign(this, { root, cache, tokens, diagnostic }); this.rows = new Map();
    }
    reset() { this.rows.clear(); this.root.replaceChildren(); this.epoch = null; }
    apply(message) {
        if (message.epoch !== this.epoch) { this.reset(); this.epoch = message.epoch; }
        if (message.replace) {
            const present = new Set(message.changes.map(e => e.key));
            for (const key of this.rows.keys()) if (!present.has(key)) this.remove(key);
        }
        for (const key of message.deleted ?? []) this.remove(key);
        for (const entity of message.changes ?? []) {
            const state = entity.components[Component.Annotation]; if (!state) continue;
            for (const [key, row] of this.rows) if (row.state.id === state.id && key !== entity.key) this.remove(key);
            let row = this.rows.get(entity.key);
            if (!row) {
                if (this.rows.size >= 64) this.remove(this.rows.keys().next().value);
                const node = document.createElement('div'), label = document.createElement('span'), distance = document.createElement('small');
                node.className = 'live-annotation'; label.dataset.vguiControl = 'CalloutLabel'; distance.dataset.vguiControl = 'DistanceLabel';
                node.append(label, distance); this.root.append(node); row = { node, label, distance }; this.rows.set(entity.key, row);
            }
            row.state = state;
        }
    }
    remove(key) { this.rows.get(key)?.node.remove(); this.rows.delete(key); }
    async load(assetSet) {
        if (!assetSet || this.assetSet === assetSet) return;
        this.assetSet = assetSet; this.images = null; this.imageRpc = null; this.root.removeAttribute('data-vgui-images'); this.abort?.abort(); const abort = this.abort = new AbortController();
        try {
            const entries = await readResource('resource/ui/annotationspanelcallout.res', path => this.cache.fetch(`/assets/${path}?assetSet=${encodeURIComponent(assetSet)}`, abort.signal));
            if (abort.signal.aborted) return;
            const panel = entries.find(e => Array.isArray(e.value))?.value;
            const label = resourceProperties(resourceValue(panel, 'calloutlabel'));
            const background = resourceProperties(resourceValue(panel, 'calloutbg'));
            const arrowIcons = resourceProperties(resourceValue(resourceValue(panel, 'annotationspanelcallout'), 'arrowicons'));
            this.images = background.image && arrowIcons.left && arrowIcons.right && arrowIcons.down
                ? { panel: background.image, left: arrowIcons.left, right: arrowIcons.right, down: arrowIcons.down } : null;
            const padding = Math.max(4, Math.min(24, Number(background.draw_corner_width) || 8));
            this.root.style.setProperty('--annotation-padding', `${padding}px`);
        } catch (error) { if (!abort.signal.aborted) this.diagnostic(`Annotation layout unavailable; using the default callout. ${error.message}`); }
    }
    prepareImages(rpc) {
        if (!rpc || !this.images || this.imageRpc === rpc) return;
        this.imageRpc = rpc;
        const generation = this.abort;
        const image = async name => {
            if (!name) return null;
            const path = materialPath(name.startsWith('../') ? name.slice(3) : `vgui/${name}`);
            const { material } = await rpc('material', { path });
            const { texture } = await rpc('texture', { path: texturePath(material.values.$basetexture), s3tc: false });
            const mip = texture.faces[0][0];
            if (!(mip.data instanceof Uint8Array) || mip.width > 1024 || mip.height > 1024) throw new Error('Invalid callout image');
            const canvas = document.createElement('canvas'); canvas.width = mip.width; canvas.height = mip.height;
            canvas.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(mip.data), mip.width, mip.height), 0, 0);
            return canvas.toDataURL();
        };
        void Promise.all(Object.entries(this.images).map(async ([kind, name]) => [kind, await image(name)])).then(images => {
            if (generation !== this.abort || generation?.signal.aborted) return;
            for (const [kind, url] of images) if (url) this.root.style.setProperty(`--annotation-${kind}`, `url("${url}")`);
            this.root.dataset.vguiImages = 'true';
        }).catch(error => { if (generation === this.abort && !generation?.signal.aborted) this.diagnostic(`Annotation images unavailable; using the default callout. ${error.message}`); });
    }
    frame(time, scene, camera) {
        this.prepareImages(scene?.rpc);
        const width = this.root.clientWidth, height = this.root.clientHeight;
        for (const [key, row] of this.rows) {
            const state = row.state, age = (time - state.time) / 1000, lifetime = state.lifetime || 1;
            if (state.lifetime >= 0 && age >= lifetime) { row.node.hidden = true; continue; }
            row.node.hidden = age < 0 || !scene?.owned;
            if (row.node.hidden) continue;
            const text = state.text.startsWith('#') ? this.tokens().get(state.text.slice(1).toLowerCase()) ?? state.text : state.text;
            if (row.label.textContent !== text) row.label.textContent = text;
            const position = annotationPosition(state, scene);
            row.distance.hidden = !(state.flags & 1);
            if (state.flags & 1) row.distance.textContent = (this.tokens().get('annotationpanel_distance') ?? '%s1 m').replace('%s1', String(Math.round(position.distanceTo(camera.position) * .0254)));
            const projected = projectAnnotation(position, camera, width, height, row.node.offsetWidth, row.node.offsetHeight);
            row.node.dataset.arrow = projected.arrow;
            row.node.style.transform = `translate(${projected.x}px, ${projected.y}px)`;
        }
    }
    dispose() { this.abort?.abort(); this.reset(); }
}
